import React, { Children, FC, ReactElement, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { A11y, SwiperOptions, Pagination, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Typography } from './Typography';
import { SliderOutlineLeft } from './icon/icons/SliderOutlineLeft';
import { SliderOutlineRight } from './icon/icons/SliderOutlineRight';
import { Spacer } from './Spacer';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import { isBrowser } from '../utils';
import HtmlContent from './Html';

export interface CardCarouselWrapperProps {
  heading?: string;
  lead?: string;
  width?: 'narrow' | 'wide';
  hideNavigation?: boolean;
  content: string;
  component?: React.ReactNode;
}

interface StyledCardCarouselWrapperSwiperProps {
  breakpoints?: SwiperOptions;
  $numberOfChildren?: number;
}

interface StyledProps {
  narrow?: boolean;
}

// Styles

const StyledCardCarouselWrapper = styled.div`
  ${({ theme: { space } }) => css`
    .swiper-slide {
      > * {
        height: 100%;
      }
    }
  `};
`;

const StyledNavigation = styled.div<StyledProps>`
  ${({ theme: { space, colors, maxMedia }, narrow }) => css`
    --swiper-navigation-size: 48px;

    .swiper-button-next,
    .swiper-button-prev {
      background: none;
      width: var(--swiper-navigation-size);
      height: var(--swiper-navigation-size);
      display: flex;
      justify-content: center;
      align-items: center;
      background-position: center center;
      background-repeat: no-repeat;
      padding: 0;
      top: ${narrow ? '-72px' : '-115px'};
      bottom: auto;
      margin-top: 0;

      &:hover {
        svg rect {
          fill: ${colors.brand.default};
        }
        svg path[fill] {
          fill: ${colors.brand.contrast};
        }
      }

      &:after {
        display: none;
        content: '';
      }

      &.swiper-button-disabled {
        opacity: 1;

        svg rect {
          stroke: ${colors.grey3.default};
        }

        svg path[fill] {
          fill: ${colors.grey3.default};
        }
      }
    }

    .swiper-button-next {
      right: 0;
    }

    .swiper-button-prev {
      right: 56px;
      left: auto;
    }

    // Tablet < styles

    @media (max-width: ${maxMedia.xl}) {
      display: none;
    }
  `}
`;

const StyledCardCarouselWrapperSwiper = styled(Swiper)<StyledCardCarouselWrapperSwiperProps>`
  ${({ theme: { space, maxMedia, colors, global }, breakpoints, $numberOfChildren }) => {
    let carouselStyles;

    if (breakpoints) {
      Object.keys(breakpoints).forEach((key, index) => {
        const breakpoint = `${key}px`;
        const { slidesPerView } = breakpoints[key];
        if (slidesPerView && $numberOfChildren && slidesPerView !== 'auto') {
          carouselStyles += `
            @media (min-width: ${breakpoint}) {
              ${StyledNavigation} {
                display: ${$numberOfChildren > slidesPerView ? 'block' : 'none'}
              }
            }`;
        }
      });
    }

    return css`
      ${carouselStyles}
      /* display: flex; */
      /* flex-direction: column-reverse; */
      max-width: 100%;
      overflow: visible;
      position: relative;

      .swiper-slide {
        height: auto;
      }

      .swiper-pagination-progressbar {
        position: relative;
        background: ${colors.grey4.default};
        border-radius: ${global.borderRadius.default};
        overflow: hidden;
        margin-top: ${space.lg};

        .swiper-pagination-progressbar-fill {
          background: ${colors.brand.default};
        }
      }

      // Mobile < styles

      @media (max-width: ${maxMedia.md}) {
        padding-right: ${space.lg};

        .swiper-pagination-progressbar {
          width: calc(100% + ${space.lg});
        }
      }
    `;
  }}
`;

const StyledHeader = styled(Spacer)<StyledProps>`
  ${({ theme: { space, media }, narrow }) => css`
    margin-bottom: ${space.lg};

    // Tablet > styles

    @media (min-width: ${media.xl}) {
      margin-bottom: ${narrow ? space.lg : space.xl};
      padding-right: 200px; // To account for arrows
    }
  `};
`;

const StyledComponent = styled.div`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.lg};
  `};
`;

// JSX

export const CardCarouselWrapper: FC<React.PropsWithChildren<CardCarouselWrapperProps>> = ({
  heading,
  lead,
  content,
  component,
  children,
  width,
  hideNavigation,
  ...rest
}) => {
  const isNarrow = width === 'narrow';
  const [controlledSwiper, setControlledSwiper] = useState<any>(null);
  const hasMount = useRef(false);
  const prevNav = useRef<HTMLButtonElement>(null);
  const nextNav = useRef<HTMLButtonElement>(null);
  const { dataLayerPush } = useDataLayerPush();

  useEffect(() => {
    if (controlledSwiper && !hasMount.current) {
      controlledSwiper.init();
      hasMount.current = true;

      setTimeout(() => {
        controlledSwiper.update();
      }, 300);
    }
  }, [controlledSwiper]);

  const opts: SwiperOptions = {
    watchOverflow: true,
    updateOnImagesReady: true,
    loop: false,
    pagination: {
      type: 'progressbar',
    },
    navigation: {
      prevEl: prevNav.current,
      nextEl: nextNav.current,
    },
    preventInteractionOnTransition: true,
    spaceBetween: 16,
    slidesPerView: 1,
    breakpoints: {
      600: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 32,
      },
      1024: {
        slidesPerView: isNarrow ? 2 : 3,
        slidesPerGroup: isNarrow ? 2 : 3,
        spaceBetween: 32,
      },
    },
  };

  if (!children || Children.count(children) < 1) {
    return null;
  }

  return (
    <StyledCardCarouselWrapper {...rest}>
      <StyledHeader narrow={isNarrow} spacing="half">
        {heading && (
          <Typography component="h2" variant={isNarrow ? 'titleLarge' : 'displaySmall'} narrow={isNarrow}>
            {heading}
          </Typography>
        )}
        {lead && (
          <Typography component="p" variant="lead" narrow={isNarrow}>
            {lead}
          </Typography>
        )}
        {content && <HtmlContent>{content}</HtmlContent>}
        {component && <StyledComponent>{component}</StyledComponent>}
      </StyledHeader>
      <StyledCardCarouselWrapperSwiper
        onSwiper={setControlledSwiper}
        wrapperTag="ul"
        modules={[Pagination, A11y, Navigation]}
        {...opts}
        breakpoints={opts?.breakpoints}
        $numberOfChildren={Children.count(children)}
        className="carousel-swiper"
      >
        {React.Children.map(children, (slide, i) => (
          <SwiperSlide key={`swiper-${i}`} tag="li">
            {React.cloneElement(slide as ReactElement, {})}
          </SwiperSlide>
        ))}

        {/* Swiper navigation controls */}
        {!hideNavigation && (
          <StyledNavigation narrow={isNarrow}>
            <button
              type="button"
              className="swiper-button-prev"
              ref={prevNav}
              aria-label="Previous"
              onClick={() =>
                dataLayerPush({
                  event: 'carousel scroll',
                  clickText: 'previous',
                  clickSection: heading,
                })
              }
            >
              <SliderOutlineLeft size="large" />
            </button>
            <button
              type="button"
              className="swiper-button-next"
              ref={nextNav}
              aria-label="Next"
              onClick={() =>
                dataLayerPush({
                  event: 'carousel scroll',
                  clickText: 'next',
                  clickSection: heading,
                })
              }
            >
              <SliderOutlineRight size="large" />
            </button>
          </StyledNavigation>
        )}
      </StyledCardCarouselWrapperSwiper>
    </StyledCardCarouselWrapper>
  );
};
